import React from 'react';
import { ProcessedLoan } from '../data/mockLoans';
import { CheckCircle2, Phone, PhoneMissed, PhoneForwarded, Clock, FileText, PauseCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface HistorySidebarProps {
  history: ProcessedLoan[];
  isOpen: boolean;
  onClose: () => void;
}
export function HistorySidebar({
  history,
  isOpen,
  onClose
}: HistorySidebarProps) {
  const getActionIcon = (loan: ProcessedLoan) => {
    if (loan.action === 'snoozed') return <PauseCircle className="w-4 h-4 text-amber-400" data-id="element-74" />;
    if (loan.action === 'noted') return <FileText className="w-4 h-4 text-stone-400" data-id="element-75" />;
    if (loan.action === 'called') {
      switch (loan.disposition) {
        case 'contacted':
          return <Phone className="w-4 h-4 text-emerald-400" data-id="element-76" />;
        case 'no_answer':
          return <PhoneMissed className="w-4 h-4 text-rose-400" data-id="element-77" />;
        case 'voicemail':
          return <PhoneForwarded className="w-4 h-4" style={{
            color: '#C5A55A'
          }} data-id="element-78" />;
        default:
          return <Phone className="w-4 h-4 text-stone-400" data-id="element-79" />;
      }
    }
    return <CheckCircle2 className="w-4 h-4 text-stone-400" data-id="element-80" />;
  };
  const getActionLabel = (loan: ProcessedLoan) => {
    if (loan.action === 'snoozed') return 'Snoozed';
    if (loan.action === 'noted') return 'Note Left';
    if (loan.action === 'called') {
      switch (loan.disposition) {
        case 'contacted':
          return 'Contacted';
        case 'no_answer':
          return 'No Answer';
        case 'voicemail':
          return 'Voicemail';
        default:
          return 'Called';
      }
    }
    return 'Processed';
  };
  return <AnimatePresence data-id="element-81">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-40" style={{
        backgroundColor: 'rgba(42, 42, 42, 0.5)',
        backdropFilter: 'blur(4px)'
      }} data-id="element-82" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-83">
            <div className="w-full max-w-lg max-h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto" style={{
          backgroundColor: '#2A2A2A'
        }} data-id="element-84">
              {/* Header */}
              <div className="p-6 flex items-center justify-between flex-shrink-0" style={{
            borderBottom: '1px solid #3A3A3A'
          }} data-id="element-85">
                <div data-id="element-86">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2" data-id="element-87">
                    <Clock className="w-5 h-5" style={{
                  color: '#C5A55A'
                }} data-id="element-88" />
                    Session History
                  </h2>
                  <p className="text-sm text-stone-400 mt-1" data-id="element-89">
                    {history.length} {history.length === 1 ? 'loan' : 'loans'}{' '}
                    processed
                  </p>
                </div>
                <button onClick={onClose} className="p-2 text-stone-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-90">
                  <X className="w-5 h-5" data-id="element-91" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#555 transparent'
          }} data-id="element-92">
                {history.length === 0 ? <div className="text-center py-12 text-stone-500 text-sm" data-id="element-93">
                    No loans processed yet.
                  </div> : history.map((item, index) => <motion.div key={`${item.loan.id}-${index}`} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              delay: index * 0.03
            }} className="rounded-lg p-4" style={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)'
            }} data-id="element-94">
                      <div className="flex justify-between items-start mb-2" data-id="element-95">
                        <div data-id="element-96">
                          <h3 className="text-sm font-medium text-stone-200" data-id="element-97">
                            {item.loan.borrowerName}
                          </h3>
                          <p className="text-xs text-stone-500" data-id="element-98">
                            {item.loan.id}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{
                  backgroundColor: '#1E1E1E',
                  border: '1px solid #3A3A3A'
                }} data-id="element-99">
                          {getActionIcon(item)}
                          <span className="text-xs font-medium text-stone-300" data-id="element-100">
                            {getActionLabel(item)}
                          </span>
                        </div>
                      </div>

                      {item.note && <p className="text-xs text-stone-400 mt-2 line-clamp-2 italic" data-id="element-101">
                          "{item.note}"
                        </p>}

                      {item.followUpTime && <div className="mt-3 text-xs flex items-center gap-1.5 px-2 py-1.5 rounded" style={{
                color: '#C5A55A',
                backgroundColor: 'rgba(197, 165, 90, 0.1)'
              }} data-id="element-102">
                          <Clock className="w-3.5 h-3.5" data-id="element-103" />
                          Follow-up:{' '}
                          {new Date(item.followUpTime).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                        </div>}

                      {item.snoozeUntil && <div className="mt-3 text-xs flex items-center gap-1.5 px-2 py-1.5 rounded bg-amber-500/10 text-amber-400" data-id="element-104">
                          <PauseCircle className="w-3.5 h-3.5" data-id="element-105" />
                          Snoozed until:{' '}
                          {new Date(item.snoozeUntil).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                        </div>}
                    </motion.div>)}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}